import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import tmi from "tmi.js";

const TwitchContext = createContext(null);

// Global event emitter for cross-component communication
const eventEmitter = {
  listeners: {},
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    return () => this.off(event, callback);
  },
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(
        (cb) => cb !== callback,
      );
    }
  },
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((callback) => callback(data));
    }
  },
};

export { eventEmitter };

// Singleton pattern to prevent multiple connections
let globalClient = null;

export function TwitchProvider({ children }) {
  const clientRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Use global singleton to prevent duplicate connections
    if (globalClient) {
      clientRef.current = globalClient;
      setIsConnected(globalClient.readyState() === "OPEN");
      return;
    }

    const client = new tmi.Client({
      connection: {
        secure: true,
        reconnect: true,
      },
      channels: ["#padrebobo"],
    });

    client.connect().catch((error) => {
      console.error("Failed to connect:", error);
    });

    client.on("message", (channel, tags, message, self) => {
      if (self) return;

      eventEmitter.emit("twitchMessage", {
        tags,
        message,
        channel,
        timestamp: new Date(),
      });
    });

    client.on("raw_message", (messageCloned, message) => {
      if (message.command === "CLEARMSG") {
        const targetMsgId = message.tags["target-msg-id"];
        eventEmitter.emit("twitchClearMsg", { targetMsgId });

        // Auto-remove from DOM
        const messageElement = document.querySelector(
          `[data-msg-id="${targetMsgId}"]`,
        );
        if (messageElement) {
          messageElement.remove();
        }
      }
    });

    client.on("connected", (address, port) => {
      console.log(`Connected to Twitch: ${address}:${port}`);
      setIsConnected(true);
    });

    client.on("disconnected", (reason) => {
      console.log(`Twitch disconnected: ${reason}`);
      setIsConnected(false);
    });

    client.on("error", (error) => {
      console.error("Twitch connection error:", error);
    });

    globalClient = client;
    clientRef.current = client;

    // No cleanup - keep connection alive
  }, []);

  const subscribe = useCallback((event, callback) => {
    return eventEmitter.on(event, callback);
  }, []);

  return (
    <TwitchContext.Provider value={{ isConnected, subscribe, eventEmitter }}>
      {children}
    </TwitchContext.Provider>
  );
}

export function useTwitch() {
  const context = useContext(TwitchContext);
  if (!context) {
    throw new Error("useTwitch must be used within a TwitchProvider");
  }
  return context;
}

export function useTwitchMessage(callback) {
  const { subscribe } = useTwitch();

  useEffect(() => {
    const unsubscribe = subscribe("twitchMessage", callback);
    return unsubscribe;
  }, [subscribe, callback]);
}
